import { AnimatePresence } from 'framer-motion'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Typography } from 'components/uiKit/Typography'
import { testProps } from 'utils/test/qaData'

import Panel from './Panel'

type FnChild = (props: { open: boolean; setOpen: (open: boolean) => void }) => React.ReactNode

interface IFloatingPanelProps {
  children: React.ReactNode | FnChild
  header?: React.ReactNode
  panel?: React.ReactNode
  onClose?: () => void
  onOpen?: () => void
  width?: number | string
  initialOpen?: boolean
  disabled?: boolean
}

const FloatingPanel: React.FC<IFloatingPanelProps> = (props) => {
  const refRoot = useRef<HTMLDivElement | null>(null)
  const { children, panel, header, width, initialOpen = false, disabled } = props
  const { onClose, onOpen } = props
  const [open, setOpen] = useState(initialOpen)

  const handleOpen = useCallback(() => {
    if (!disabled) {
      onOpen?.()
      setOpen(true)
    }
  }, [onOpen, disabled])

  const handleClose = useCallback(() => {
    onClose?.()
    setOpen(false)
  }, [onClose])

  const handleToggle = useCallback(
    (open: boolean) => (open ? handleClose() : handleOpen()),
    [handleClose, handleOpen],
  )

  const childClonedEl = useMemo(() => {
    const temp =
      typeof children === 'function' ? children({ open, setOpen: handleToggle }) : children
    const child = React.isValidElement(temp) ? (
      temp
    ) : (
      <Typography onClick={handleOpen} ref={refRoot}>
        {temp}
      </Typography>
    )

    return React.cloneElement(child, {
      ...child?.props,
      ...testProps({ el: 'floatingPanel', name, open }),
      ref: child.props.ref || refRoot,
    })
  }, [handleOpen, handleToggle, children, open])

  useEffect(() => {
    const rootCurrentRef = refRoot.current
    if (!rootCurrentRef) {
      return
    }

    const abortController = new AbortController()
    const signal = abortController.signal

    rootCurrentRef.addEventListener('click', () => (open ? handleClose() : handleOpen()), {
      signal,
    })

    return () => {
      abortController.abort()
    }
  }, [handleClose, handleOpen, children, open])

  return (
    <>
      {childClonedEl}
      <AnimatePresence>
        {open && (
          <Panel header={header} onClose={handleClose} refRoot={refRoot} width={width}>
            {panel}
          </Panel>
        )}
      </AnimatePresence>
    </>
  )
}

export default React.memo(FloatingPanel) as typeof FloatingPanel
